import { useMemo, useState } from "react";
import { api } from "../api";
import { formatCurrency } from "../format";
import type { Field, Row } from "../types";
import { IconEdit, IconPlus, IconSearch, IconTrash } from "../icons";
import RecordForm from "./RecordForm";
import { compararNomes } from "../ordem";
import {
  CAMPOS_ETAPA,
  CAMPOS_NIVEL,
  CAMPOS_TEMA,
  CAMPOS_TRILHA,
  NOTAS_PLANO,
  aumentoSobreAnterior,
  camposBase,
  norm,
} from "../plano-cargos";
import type { Plano } from "./CargosView";

/**
 * Seções do Plano de Cargos e Salários que vieram das abas "Trilha ...",
 * "Critérios e Uso" e "Base Vigente" da planilha. Ver CargosView.tsx.
 */

interface SecaoProps {
  plano: Plano;
  podeEditar: boolean;
  podeExcluir: boolean;
  recarregar: () => Promise<void>;
}

/** Formulário aberto: qual recurso, qual registro e o que vai junto ao criar. */
interface Edicao {
  resource: string;
  titulo: string;
  campos: Field[];
  registro: Row | null;
  extra?: Row;
}

function useEdicao(recarregar: () => Promise<void>) {
  const [edicao, setEdicao] = useState<Edicao | null>(null);

  async function salvar(data: Row) {
    if (!edicao) return;
    if (edicao.registro) await api.update(edicao.resource, edicao.registro.id, data);
    else await api.create(edicao.resource, { ...data, ...edicao.extra });
    setEdicao(null);
    await recarregar();
  }

  async function excluir(resource: string, row: Row, descricao: string) {
    if (!confirm(`Excluir ${descricao}?`)) return;
    try {
      await api.remove(resource, row.id);
      await recarregar();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  const formulario = edicao && (
    <RecordForm
      title={edicao.titulo}
      fields={edicao.campos}
      initial={edicao.registro}
      colaboradores={[]}
      indiceCargos={null}
      onCancel={() => setEdicao(null)}
      onSubmit={salvar}
    />
  );

  return { abrir: setEdicao, excluir, formulario };
}

/** Lápis e lixeira de uma linha (cada um só para quem pode). */
function Acoes({
  podeEditar,
  podeExcluir,
  onEditar,
  onExcluir,
}: {
  podeEditar: boolean;
  podeExcluir: boolean;
  onEditar: () => void;
  onExcluir: () => void;
}) {
  if (!podeEditar && !podeExcluir) return null;
  return (
    <span className="cargo-acoes">
      {podeEditar && (
        <button className="icon-btn" title="Editar" onClick={onEditar}>
          <IconEdit size={15} />
        </button>
      )}
      {podeExcluir && (
        <button className="icon-btn icon-btn-danger" title="Excluir" onClick={onExcluir}>
          <IconTrash size={15} />
        </button>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------------ */
/* Etapas de uma trilha (usado na ficha do cargo e na seção Trilhas)         */
/* ------------------------------------------------------------------------ */

/** A etapa que é o próprio cargo: mesmo nome, ou o nome + o mesmo nível. */
function ehEtapaDoCargo(etapa: Row, cargo: Row | undefined): boolean {
  if (!cargo) return false;
  const e = norm(etapa.cargo);
  const c = norm(cargo.cargo);
  return e === c || (e.startsWith(c) && norm(etapa.nivel) === norm(cargo.nivel));
}

export function TrilhaEtapas({
  etapas,
  destaque,
  observacao,
  abertas = false,
  podeEditar = false,
  podeExcluir = false,
  onEditar,
  onExcluir,
}: {
  etapas: Row[];
  destaque?: Row;
  observacao?: unknown;
  abertas?: boolean;
  podeEditar?: boolean;
  podeExcluir?: boolean;
  onEditar?: (e: Row) => void;
  onExcluir?: (e: Row) => void;
}) {
  const ordenadas = [...etapas].sort((a, b) => (Number(a.etapa) || 0) - (Number(b.etapa) || 0));
  if (ordenadas.length === 0) return <p className="plano-nota">Nenhuma etapa cadastrada nesta trilha.</p>;
  return (
    <div className="plano-etapas">
      {ordenadas.map((e, i) => {
        const atual = ehEtapaDoCargo(e, destaque);
        const aumento = i > 0 ? aumentoSobreAnterior(e.salario_sugerido, ordenadas[i - 1].salario_sugerido) : null;
        return (
          <details key={e.id} className={atual ? "plano-etapa atual" : "plano-etapa"} open={abertas || atual}>
            <summary>
              <span className="plano-etapa-num">{String(e.etapa ?? i + 1)}</span>
              <span className="plano-etapa-nome">
                <b>{String(e.cargo ?? "")}</b>
                <small>
                  {String(e.nivel ?? "")}
                  {atual ? " · cargo atual" : ""}
                </small>
              </span>
              <span className="plano-etapa-valor">
                <b>{formatCurrency(e.salario_sugerido)}</b>
                <small>{aumento ? `${aumento} sobre a etapa anterior` : "salário sugerido"}</small>
              </span>
              {(podeEditar || podeExcluir) && (
                <span onClick={(ev) => ev.preventDefault()}>
                  <Acoes
                    podeEditar={podeEditar}
                    podeExcluir={podeExcluir}
                    onEditar={() => onEditar?.(e)}
                    onExcluir={() => onExcluir?.(e)}
                  />
                </span>
              )}
            </summary>
            <dl className="plano-ficha-lista plano-etapa-detalhe">
              {(
                [
                  ["objetivo", "Objetivo do nível"],
                  ["atribuicoes", "Atribuições crescentes"],
                  ["formacao", "Formação"],
                  ["conhecimentos", "Conhecimentos"],
                  ["experiencia", "Experiência"],
                  ["criterio_avanco", "Critério de avanço"],
                ] as const
              ).map(([campo, rotulo]) => (
                <div key={campo}>
                  <dt>{rotulo}</dt>
                  <dd>{e[campo] ? String(e[campo]) : "—"}</dd>
                </div>
              ))}
            </dl>
          </details>
        );
      })}
      {observacao ? <p className="plano-nota">{String(observacao)}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Trilhas de crescimento (as 24 abas "Trilha ...")                          */
/* ------------------------------------------------------------------------ */

export function TrilhasPlano({ plano, podeEditar, podeExcluir, recarregar }: SecaoProps) {
  const trilhas = useMemo(
    () => [...plano.trilhas].sort((a, b) => (Number(a.ordem) || 9999) - (Number(b.ordem) || 9999)),
    [plano.trilhas],
  );
  const [selecionadaId, setSelecionadaId] = useState<number | null>(null);
  const trilha = trilhas.find((t) => Number(t.id) === selecionadaId) ?? trilhas[0] ?? null;
  const etapas = trilha ? plano.etapas.filter((e) => Number(e.trilha_id) === Number(trilha.id)) : [];
  const cargo = trilha ? plano.cargos.find((c) => norm(c.cargo) === norm(trilha.cargo_vigente)) : undefined;
  const { abrir, excluir, formulario } = useEdicao(recarregar);

  const proximaEtapa = etapas.reduce((m, e) => Math.max(m, Number(e.etapa) || 0), 0) + 1;

  return (
    <>
      <div className="crud-toolbar">
        <p className="plano-nota plano-nota-barra">{NOTAS_PLANO.trilhas}</p>
        {podeEditar && (
          <div className="toolbar-right">
            <button
              className="btn-primary"
              onClick={() =>
                abrir({
                  resource: "cargos_trilhas",
                  titulo: "Nova trilha de crescimento",
                  campos: CAMPOS_TRILHA,
                  registro: null,
                  extra: { observacao: NOTAS_PLANO.trilhas },
                })
              }
            >
              <IconPlus size={17} /> Nova trilha
            </button>
          </div>
        )}
      </div>

      {trilhas.length === 0 ? (
        <div className="empty">Nenhuma trilha cadastrada.</div>
      ) : (
        <div className="plano-trilhas">
          <nav className="plano-trilhas-lista" aria-label="Trilhas">
            <select
              className="plano-trilhas-select"
              value={trilha ? String(trilha.id) : ""}
              onChange={(e) => setSelecionadaId(Number(e.target.value))}
            >
              {trilhas.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {String(t.cargo_vigente)}
                </option>
              ))}
            </select>
            {trilhas.map((t) => (
              <button
                key={t.id}
                className={trilha && Number(t.id) === Number(trilha.id) ? "ativa" : ""}
                onClick={() => setSelecionadaId(Number(t.id))}
              >
                <b>{String(t.cargo_vigente)}</b>
                <small>
                  {String(t.familia ?? "")} · {plano.etapas.filter((e) => Number(e.trilha_id) === Number(t.id)).length}{" "}
                  etapas
                </small>
              </button>
            ))}
          </nav>

          {trilha && (
            <section className="plano-trilha">
              <div className="plano-trilha-head">
                <div>
                  <h3>{String(trilha.cargo_vigente)}</h3>
                  <p className="modal-sub">Família: {String(trilha.familia ?? "—")}</p>
                </div>
                <span className="toolbar-right">
                  {podeEditar && (
                    <>
                      <button
                        className="btn-secondary"
                        onClick={() =>
                          abrir({
                            resource: "cargos_trilhas",
                            titulo: `Editar trilha — ${trilha.cargo_vigente}`,
                            campos: CAMPOS_TRILHA,
                            registro: trilha,
                          })
                        }
                      >
                        <IconEdit size={16} /> Editar trilha
                      </button>
                      <button
                        className="btn-primary"
                        onClick={() =>
                          abrir({
                            resource: "cargos_trilha_etapas",
                            titulo: `Nova etapa — ${trilha.cargo_vigente}`,
                            campos: CAMPOS_ETAPA,
                            registro: null,
                            extra: { trilha_id: trilha.id, etapa: proximaEtapa },
                          })
                        }
                      >
                        <IconPlus size={16} /> Etapa
                      </button>
                    </>
                  )}
                  {podeExcluir && (
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Excluir a trilha inteira"
                      onClick={() => excluir("cargos_trilhas", trilha, `a trilha de "${trilha.cargo_vigente}" com todas as etapas`)}
                    >
                      <IconTrash size={16} />
                    </button>
                  )}
                </span>
              </div>
              <TrilhaEtapas
                etapas={etapas}
                destaque={cargo}
                observacao={trilha.observacao !== NOTAS_PLANO.trilhas ? trilha.observacao : null}
                abertas
                podeEditar={podeEditar}
                podeExcluir={podeExcluir}
                onEditar={(e) =>
                  abrir({
                    resource: "cargos_trilha_etapas",
                    titulo: `Editar etapa ${e.etapa} — ${e.cargo}`,
                    campos: CAMPOS_ETAPA,
                    registro: e,
                  })
                }
                onExcluir={(e) => excluir("cargos_trilha_etapas", e, `a etapa "${e.cargo}"`)}
              />
            </section>
          )}
        </div>
      )}
      {formulario}
    </>
  );
}

/* ------------------------------------------------------------------------ */
/* Critérios e uso                                                           */
/* ------------------------------------------------------------------------ */

export function CriteriosPlano({ plano, podeEditar, podeExcluir, recarregar }: SecaoProps) {
  const { abrir, excluir, formulario } = useEdicao(recarregar);
  const porOrdem = (a: Row, b: Row) => (Number(a.ordem) || 9999) - (Number(b.ordem) || 9999);
  const temas = plano.criterios.filter((c) => c.tipo === "tema").sort(porOrdem);
  const niveis = plano.criterios.filter((c) => c.tipo === "nivel").sort(porOrdem);
  const proxima = (l: Row[]) => l.reduce((m, c) => Math.max(m, Number(c.ordem) || 0), 0) + 1;

  return (
    <div className="plano-criterios">
      <section>
        <div className="plano-bloco-head">
          <h3>Critérios de enquadramento e uso</h3>
          {podeEditar && (
            <button
              className="btn-secondary"
              onClick={() =>
                abrir({
                  resource: "cargos_criterios",
                  titulo: "Novo tema",
                  campos: CAMPOS_TEMA,
                  registro: null,
                  extra: { tipo: "tema", ordem: proxima(temas) },
                })
              }
            >
              <IconPlus size={16} /> Tema
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table className="table plano-tabela">
            <thead>
              <tr>
                <th>Tema</th>
                <th>Diretriz</th>
                {(podeEditar || podeExcluir) && <th className="col-actions" />}
              </tr>
            </thead>
            <tbody>
              {temas.map((t) => (
                <tr key={t.id}>
                  <td className="plano-forte">{String(t.titulo)}</td>
                  <td className="quebra">{String(t.texto ?? "")}</td>
                  {(podeEditar || podeExcluir) && (
                    <td className="col-actions">
                      <Acoes
                        podeEditar={podeEditar}
                        podeExcluir={podeExcluir}
                        onEditar={() =>
                          abrir({ resource: "cargos_criterios", titulo: `Editar tema — ${t.titulo}`, campos: CAMPOS_TEMA, registro: t })
                        }
                        onExcluir={() => excluir("cargos_criterios", t, `o tema "${t.titulo}"`)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="plano-bloco-head">
          <h3>Níveis de carreira</h3>
          {podeEditar && (
            <button
              className="btn-secondary"
              onClick={() =>
                abrir({
                  resource: "cargos_criterios",
                  titulo: "Novo nível",
                  campos: CAMPOS_NIVEL,
                  registro: null,
                  extra: { tipo: "nivel", ordem: proxima(niveis) },
                })
              }
            >
              <IconPlus size={16} /> Nível
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table className="table plano-tabela">
            <thead>
              <tr>
                <th>Nível</th>
                <th>Autonomia esperada</th>
                <th>Critério típico</th>
                {(podeEditar || podeExcluir) && <th className="col-actions" />}
              </tr>
            </thead>
            <tbody>
              {niveis.map((n) => (
                <tr key={n.id}>
                  <td className="plano-forte">{String(n.titulo)}</td>
                  <td className="quebra">{String(n.texto ?? "")}</td>
                  <td className="quebra">{String(n.texto2 ?? "")}</td>
                  {(podeEditar || podeExcluir) && (
                    <td className="col-actions">
                      <Acoes
                        podeEditar={podeEditar}
                        podeExcluir={podeExcluir}
                        onEditar={() =>
                          abrir({ resource: "cargos_criterios", titulo: `Editar nível — ${n.titulo}`, campos: CAMPOS_NIVEL, registro: n })
                        }
                        onExcluir={() => excluir("cargos_criterios", n, `o nível "${n.titulo}"`)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {formulario}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Base vigente                                                              */
/* ------------------------------------------------------------------------ */

export function BaseVigentePlano({ plano, podeEditar, podeExcluir, recarregar }: SecaoProps) {
  const { abrir, excluir, formulario } = useEdicao(recarregar);
  const [search, setSearch] = useState("");
  const nomes = useMemo(
    () => [...new Set(plano.cargos.map((c) => String(c.cargo ?? "")).filter(Boolean))].sort(compararNomes),
    [plano.cargos],
  );
  const campos = useMemo(() => camposBase(nomes), [nomes]);
  const linhas = useMemo(() => {
    const q = norm(search);
    const todas = [...plano.base].sort((a, b) => (Number(a.ordem) || 9999) - (Number(b.ordem) || 9999));
    return q ? todas.filter((b) => [b.cargo, b.cbo, b.contrato].some((v) => norm(v).includes(q))) : todas;
  }, [plano.base, search]);
  const proxima = plano.base.reduce((m, b) => Math.max(m, Number(b.ordem) || 0), 0) + 1;
  const foraDoPlano = (cargo: unknown) => !plano.cargos.some((c) => norm(c.cargo) === norm(cargo));

  return (
    <>
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar cargo, CBO ou contrato..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{linhas.length} pessoa(s)</span>
          {podeEditar && (
            <button
              className="btn-primary"
              onClick={() =>
                abrir({
                  resource: "cargos_base_vigente",
                  titulo: "Nova linha da base vigente",
                  campos,
                  registro: null,
                  extra: { ordem: proxima },
                })
              }
            >
              <IconPlus size={17} /> Nova linha
            </button>
          )}
        </div>
      </div>
      <p className="plano-nota">
        Salário-base de cada ocupante, sem nomes. É desta lista que saem a quantidade e os salários mínimo, médio e
        máximo vigentes de cada cargo — corrigir aqui corrige o cargo.
      </p>
      <div className="table-wrap">
        <table className="table plano-tabela">
          <thead>
            <tr>
              <th>Cargo padronizado</th>
              <th>CBO informado</th>
              <th>Contrato</th>
              <th className="num">Salário-base vigente</th>
              {(podeEditar || podeExcluir) && <th className="col-actions" />}
            </tr>
          </thead>
          <tbody>
            {linhas.map((b) => (
              <tr key={b.id}>
                <td>
                  {String(b.cargo ?? "")}
                  {foraDoPlano(b.cargo) && <small className="plano-aviso"> fora do plano — não entra na conta</small>}
                </td>
                <td>{String(b.cbo ?? "—")}</td>
                <td>{String(b.contrato ?? "—")}</td>
                <td className="num">{formatCurrency(b.salario_base)}</td>
                {(podeEditar || podeExcluir) && (
                  <td className="col-actions">
                    <Acoes
                      podeEditar={podeEditar}
                      podeExcluir={podeExcluir}
                      onEditar={() =>
                        abrir({ resource: "cargos_base_vigente", titulo: `Editar — ${b.cargo}`, campos, registro: b })
                      }
                      onExcluir={() => excluir("cargos_base_vigente", b, `esta linha de "${b.cargo}"`)}
                    />
                  </td>
                )}
              </tr>
            ))}
            {linhas.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Nada encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {formulario}
    </>
  );
}
